import React from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Box, Typography, Button, Paper } from '@mui/material';
import { Lock as LockIcon } from '@mui/icons-material';
import { ROUTES_NAMES } from './Layout';
import { canOpenExplorerSection, getAccessibleExplorerTabs, getActiveExplorerTab } from '../pages/Explorer/explorerTabs';
import { useExplorerClipsIa } from '../pages/Explorer/useExplorerClipsIa';
import { useExplorerSessionActivity } from '../pages/Explorer/useExplorerSessionActivity';
import { EXPLORER_TABLE_DATA_COL_WIDTH } from '../pages/Explorer/utils';

interface ExplorerRouteProps {
    children: React.ReactNode;
}

/**
 * Route wrapper for Explorer product pages.
 * Allows access for:
 * - Platform admins (role === 'admin')
 * - Users whose company has 'explorer' product enabled
 * Pages of a section the account cannot open (e.g. a program URL without Programas) redirect to
 * the first section it can open.
 */
const ExplorerRoute: React.FC<ExplorerRouteProps> = ({ children }) => {
    const { isAuthenticated, hasAnyExplorerAccess, hasExplorerTabAccess, getDefaultRoute } =
        useAuth();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const clipsIa = useExplorerClipsIa();

    if (!isAuthenticated) {
        return <Navigate to={ROUTES_NAMES.AUTH} replace />;
    }

    if (!hasAnyExplorerAccess()) {
        const defaultRoute = getDefaultRoute();

        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '100vh',
                    backgroundColor: '#ffffff',
                    p: 3,
                }}
            >
                <Paper
                    elevation={3}
                    sx={{
                        p: 4,
                        maxWidth: 450,
                        textAlign: 'center',
                        borderRadius: 2,
                    }}
                >
                    <LockIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h5" gutterBottom fontWeight="bold">
                        Explorador no disponible
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                        No tienes acceso al producto Explorador.
                    </Typography>
                    {defaultRoute !== '/auth' && (
                        <Button
                            variant="contained"
                            onClick={() => navigate(defaultRoute)}
                            sx={{ mt: 1 }}
                        >
                            Ir a inicio
                        </Button>
                    )}
                </Paper>
            </Box>
        );
    }

    const accessibleTabs = getAccessibleExplorerTabs(hasExplorerTabAccess);
    const activeTab = getActiveExplorerTab(pathname);
    if (accessibleTabs.length > 0 && !canOpenExplorerSection(activeTab, hasExplorerTabAccess, clipsIa)) {
        return <Navigate to={accessibleTabs[0]!.path} replace />;
    }

    return <ExplorerSessionShell>{children}</ExplorerSessionShell>;
};

const ExplorerSessionShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    useExplorerSessionActivity();
    return <Box sx={EXPLORER_PAGE_SX}>{children}</Box>;
};

/** `display: contents` keeps page layout untouched while scoping shared Explorer table rules. */
const EXPLORER_PAGE_SX = {
    display: 'contents',
    '& .MuiTableCell-alignRight:not([colspan])': {
        width: EXPLORER_TABLE_DATA_COL_WIDTH,
        minWidth: EXPLORER_TABLE_DATA_COL_WIDTH,
        whiteSpace: 'nowrap',
    },
    // Skeletons are block elements; align them like the value they stand in for.
    '& .MuiTableCell-alignRight > .MuiSkeleton-root': { marginLeft: 'auto' },
    '& .MuiTableCell-alignCenter > .MuiSkeleton-root': { marginLeft: 'auto', marginRight: 'auto' },
} as const;

export default ExplorerRoute;
